import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { notificar } from '../utils/notify';
import '../styles/posicao.css';
import '../styles/fmp.css';

import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line,
  LabelList
} from 'recharts';

// Classificação da conta na Posição: o cadastro grava a categoria com o texto da
// tela ("Cartão de Crédito", "Conta Aplicação"...), então comparar com 'credito'
// nunca casava e o cartão entrava como dinheiro disponível. Comparação sem acento
// e por conteúdo; `tipo` é reserva quando a categoria vem vazia.
const classeConta = (conta) => {
  const norm = (v) => (v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  for (const valor of [norm(conta?.categoria), norm(conta?.tipo)]) {
    if (!valor) continue;
    if (valor.includes('credito') || valor.includes('cartao')) return 'credito';
    if (valor.includes('investimento') || valor.includes('aplicacao') || valor.startsWith('cdb')) return 'investimento';
    return 'corrente';
  }
  return 'corrente';
};

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseFloat(valor));
};

// Versão compacta para os eixos do gráfico (ex.: R$ 232,7 mil)
const formatarValorCompacto = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(parseFloat(valor));
};

// Cores do waterfall na paleta FMP (gradientes definidos no <defs> do gráfico)
const corBarraWaterfall = (item, index) => {
  const mapa = {
    'Contas Correntes/Digitais': 'url(#wfCyan)',
    'A Receber': 'url(#wfGreen)',
    'A Pagar': 'url(#wfRed)',
    'Saldo Final Projetado': 'url(#wfNavy)',
  };
  const fallback = ['url(#wfCyan)', 'url(#wfGreen)', 'url(#wfRed)', 'url(#wfNavy)'];
  return mapa[item] || fallback[index % fallback.length];
};

const Posicao_cliente = () => {
  const { cliente_id } = useParams(); // Pega ID da URL
  const navigate = useNavigate();
  
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [dadosPosicao, setDadosPosicao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [mostrarContasPagar, setMostrarContasPagar] = useState(false);
  const [mostrarContasReceber, setMostrarContasReceber] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [mensagemEmail, setMensagemEmail] = useState('');
  const [observacao, setObservacao] = useState('');
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [carregandoPreview, setCarregandoPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewAssunto, setPreviewAssunto] = useState('');
  const [previewDest, setPreviewDest] = useState('');
  const [confirmacaoMarcada, setConfirmacaoMarcada] = useState(false);

  // Exclusão manual de lançamentos (quando o webhook do ERP falha)
  const [selPagar, setSelPagar] = useState([]);
  const [selReceber, setSelReceber] = useState([]);

  // Pagamentos agendados no banco (marcação do operador, só na sessão; vai no relatório)
  const [agendados, setAgendados] = useState([]);
  const toggleAgendado = (codigo) => {
    if (!codigo) return;
    setAgendados((prev) => (prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]));
  };
  // contas a pagar já com a flag "agendado" (para enviar no payload do relatório)
  const contasPagarComAgendado = () =>
    (dadosPosicao?.contas_pagar || []).map((c) => ({ ...c, agendado: agendados.includes(c.codigo_lancamento) }));
  const [deletando, setDeletando] = useState(false);
  const [mensagemDelete, setMensagemDelete] = useState('');
  
  // NOVOS STATES PARA HISTÓRICO
  const [abaAtiva, setAbaAtiva] = useState('posicao'); // 'posicao' ou 'historico'
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [mensagemHistorico, setMensagemHistorico] = useState('');
  const [sistemaCliente, setSistemaCliente] = useState(null);

  const waterfallChartRef = useRef(null);

  // Descobre se o cliente é Omie ou Conta Azul para saber pra qual dashboard voltar
  useEffect(() => {
    if (!cliente_id) return;
    const token = localStorage.getItem('access');
    axios.get(
      `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    )
      .then((response) => {
        if (response.data.success) {
          setSistemaCliente(response.data.cliente?.sistema || 'omie');
        }
      })
      .catch(() => {});
  }, [cliente_id]);

  const irParaDashboard = () => {
    if (sistemaCliente === 'conta_azul') {
      navigate(`/dashboard-conta-azul/${cliente_id}`);
    } else {
      navigate(`/dashboard-cliente/${cliente_id}`);
    }
  };

  useEffect(() => { notificar(mensagem); }, [mensagem]);
  useEffect(() => { notificar(mensagemEmail); }, [mensagemEmail]);
  useEffect(() => { notificar(mensagemHistorico); }, [mensagemHistorico]);
  useEffect(() => { notificar(mensagemDelete); }, [mensagemDelete]);

  useEffect(() => {
    const hoje = new Date();
    const inicioSemana = new Date(hoje);
    inicioSemana.setDate(hoje.getDate() - hoje.getDay() + 1);
    
    const fimSemana = new Date(inicioSemana);
    fimSemana.setDate(inicioSemana.getDate() + 6);
    
    setDataInicial(formatarDataInput(inicioSemana));
    setDataFinal(formatarDataInput(fimSemana));
  }, []);

  // NOVA FUNÇÃO: BUSCAR HISTÓRICO
// NOVA FUNÇÃO: BUSCAR HISTÓRICO
const buscarHistorico = async () => {
  setCarregandoHistorico(true);
  setMensagemHistorico('Carregando histórico...');
  
  const token = localStorage.getItem('access'); // ADICIONAR ESTA LINHA
  
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE}/api/historico-posicoes-cliente/`,
      {
        params: { cliente_id: cliente_id },
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );
    
    if (response.data.success) {
      setHistorico(response.data.historico);
      setMensagemHistorico(
        response.data.historico.length > 0 
          ? `${response.data.historico.length} registro(s) encontrado(s)` 
          : 'Nenhum histórico encontrado'
      );
      setTimeout(() => setMensagemHistorico(''), 3000);
    } else {
      setMensagemHistorico(`${response.data.error}`);
    }
  } catch (error) {
    console.error('Erro ao buscar histórico:', error);
    setMensagemHistorico('Erro ao carregar histórico');
  } finally {
    setCarregandoHistorico(false);
  }
};

  // CARREGAR HISTÓRICO AO MUDAR PARA ABA HISTÓRICO
  useEffect(() => {
    if (abaAtiva === 'historico' && historico.length === 0) {
      buscarHistorico();
    }
  }, [abaAtiva]);

  const WaterfallTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const valor = payload[0].value;

      return (
        <div
          style={{
            backgroundColor: '#00205b',
            padding: '35px 25px',
            borderRadius: '8px',
            color: '#ffffff',
            boxShadow: '0 6px 20px rgba(243, 247, 18, 0.25)',
            fontSize: '14px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
            Etapa: {label}
          </div>

          <div>
            Valor: {formatarValor(valor)}
          </div>
        </div>
      );
    }

    return null;
  };

  const formatarDataInput = (data) => {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const formatarDataBR = (dataInput) => {
    if (!dataInput) return '';
    const [ano, mes, dia] = dataInput.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const consultarPosicao = async () => {
    if (!dataInicial || !dataFinal) {
      setMensagem('Selecione o período completo');
      return;
    }

    setCarregando(true);
    setMensagem('Calculando posição financeira...');

    const token = localStorage.getItem('access');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/posicao-financeira-cliente/`,
        {
          cliente_id: cliente_id, // Envia cliente_id
          data_inicial: formatarDataBR(dataInicial),
          data_final: formatarDataBR(dataFinal)
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 0
        }
      );

      if (response.data.success) {
        console.log('Posição financeira:', response.data.dados);
        setDadosPosicao(response.data.dados);
        setMensagem('Posição calculada com sucesso!');
        setTimeout(() => setMensagem(''), 3000);
      } else {
        setMensagem(`Erro: ${response.data.erro}`);
      }

    } catch (error) {
      console.error('Erro:', error);
      setMensagem('Erro ao conectar com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  // Marca/desmarca um lançamento para exclusão
  const toggleSel = (tipo, codigo) => {
    if (!codigo) return;
    if (tipo === 'pagar') {
      setSelPagar((prev) => prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]);
    } else {
      setSelReceber((prev) => prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]);
    }
  };

  // Exclui do banco os lançamentos selecionados (uso quando o webhook do ERP falha)
  const deletarSelecionados = async (tipo) => {
    const codigos = tipo === 'pagar' ? selPagar : selReceber;
    if (!codigos || codigos.length === 0) {
      setMensagemDelete('Selecione ao menos um lançamento para excluir');
      setTimeout(() => setMensagemDelete(''), 3000);
      return;
    }
    const label = tipo === 'pagar' ? 'a pagar' : 'a receber';
    const confirmar = window.confirm(
      `Excluir ${codigos.length} lançamento(s) ${label}?\n\n` +
      `Esta ação remove o(s) título(s) diretamente do banco de dados e NÃO pode ser desfeita.`
    );
    if (!confirmar) return;

    setDeletando(true);
    setMensagemDelete('Excluindo lançamento(s)...');
    const token = localStorage.getItem('access');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/deletar-lancamento-cliente/`,
        { cliente_id: cliente_id, tipo: tipo, codigos: codigos },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMensagemDelete(response.data.mensagem || `${response.data.removidos} lançamento(s) excluído(s)`);
        if (tipo === 'pagar') setSelPagar([]); else setSelReceber([]);
        await consultarPosicao(); // recarrega a posição já sem os títulos removidos
      } else {
        setMensagemDelete(`${response.data.erro}`);
      }
    } catch (error) {
      console.error('Erro ao excluir lançamento:', error);
      setMensagemDelete('Erro ao excluir lançamento');
    } finally {
      setDeletando(false);
    }
  };

  // Abre o modal e busca a PRÉVIA REAL do e-mail (mesmo HTML que o cliente recebe)
  const abrirPreviewEmail = async () => {
    if (!dadosPosicao) {
      setMensagemEmail('É necessário calcular a posição antes de enviar');
      setTimeout(() => setMensagemEmail(''), 3000);
      return;
    }

    setPreviewHtml('');
    setPreviewAssunto('');
    setPreviewDest('');
    setConfirmacaoMarcada(false);
    setMostrarConfirmacao(true);
    setCarregandoPreview(true);

    const token = localStorage.getItem('access');

    try {
      const imagensBase64 = [];
      if (waterfallChartRef.current) {
        const canvasWaterfall = await html2canvas(waterfallChartRef.current, {
          scale: 3,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true
        });
        imagensBase64.push(canvasWaterfall.toDataURL('image/png'));
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/enviar-email-posicao-financeira-cliente/`,
        {
          cliente_id: cliente_id,
          resumo: dadosPosicao.resumo_posicao,
          imagens: imagensBase64,
          periodo: dadosPosicao.periodo,
          contas_pagar: contasPagarComAgendado(),
          contas_receber: dadosPosicao.contas_receber || [],
          contas_bancarias: dadosPosicao.contas_bancarias || [],
          observacao: observacao.trim() || null,
          preview: true
        },
        {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 60000
        }
      );

      if (response.data.success) {
        setPreviewHtml(response.data.html || '');
        setPreviewAssunto(response.data.assunto || '');
        setPreviewDest((response.data.destinatarios || []).join(', '));
      } else {
        setMensagemEmail(`${response.data.error}`);
        setMostrarConfirmacao(false);
      }
    } catch (error) {
      console.error('Erro ao gerar prévia do email:', error);
      setMensagemEmail('Erro ao gerar a prévia do email');
      setMostrarConfirmacao(false);
    } finally {
      setCarregandoPreview(false);
    }
  };

  const enviarPosicaoEmail = async () => {
    if (!dadosPosicao) {
      setMensagemEmail('É necessário calcular a posição antes de enviar');
      setTimeout(() => setMensagemEmail(''), 3000);
      return;
    }
    
    setMostrarConfirmacao(false);
    setEnviandoEmail(true);
    setMensagemEmail('Capturando gráfico e preparando email...');
    
    try {
      const imagensBase64 = [];
      
      if (waterfallChartRef.current) {
        const canvasWaterfall = await html2canvas(waterfallChartRef.current, {
          scale: 3,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true
        });
        const imagemWaterfall = canvasWaterfall.toDataURL('image/png');
        imagensBase64.push(imagemWaterfall);
        console.log('Gráfico Waterfall capturado');
      }
      
      if (imagensBase64.length === 0) {
        throw new Error('Nenhum gráfico foi capturado');
      }
      
      setMensagemEmail('Enviando email para o cliente...');
      
      const payload = {
        cliente_id: cliente_id,
        resumo: dadosPosicao.resumo_posicao,
        imagens: imagensBase64,
        periodo: dadosPosicao.periodo,
        contas_pagar: contasPagarComAgendado(),
        contas_receber: dadosPosicao.contas_receber || [],
        contas_bancarias: dadosPosicao.contas_bancarias || [],
        observacao: observacao.trim() || null
      };
      
      console.log('DADOS SENDO ENVIADOS:');
      console.log('  - Cliente ID:', cliente_id);
      console.log('  - Contas a Pagar:', payload.contas_pagar.length, 'títulos');
      console.log('  - Contas a Receber:', payload.contas_receber.length, 'títulos');
      console.log('  - Observação:', payload.observacao);
      
      const token = localStorage.getItem('access');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/enviar-email-posicao-financeira-cliente/`,
        payload,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        }
      );
      
      if (response.data.success) {
        setMensagemEmail(`${response.data.message}`);
        setObservacao('');
        console.log('Email enviado com sucesso!');
        
        // ATUALIZAR HISTÓRICO APÓS ENVIO
        if (abaAtiva === 'historico') {
          buscarHistorico();
        }
      } else {
        setMensagemEmail(`Erro: ${response.data.error}`);
      }
      
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      
      if (error.code === 'ECONNABORTED') {
        setMensagemEmail('Timeout: O envio demorou muito. Tente novamente.');
      } else if (error.response) {
        setMensagemEmail(`Erro do servidor: ${error.response.data.error || 'Erro desconhecido'}`);
      } else if (error.request) {
        setMensagemEmail('Sem resposta do servidor. Verifique sua conexão.');
      } else {
        setMensagemEmail(`Erro: ${error.message}`);
      }
    } finally {
      setEnviandoEmail(false);
      setTimeout(() => setMensagemEmail(''), 8000);
    }
  };

  const CustomTooltipDiversificacao = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const valorReal = payload[0].payload.valorReal;
      const nome = payload[0].payload.nome;
      const negativo = valorReal < 0;
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{ color: '#ffffff', margin: '0 0 8px 0', fontWeight: 600, fontSize: '13px' }}>
            {nome} {negativo && <span style={{ color: '#ff6b6b', fontSize: '11px' }}>(negativo)</span>}
          </p>
          <p style={{ color: '#ffffff', margin: 0, fontWeight: 500, fontSize: '17px' }}>
            Valor: <span style={{ color: negativo ? '#ff6b6b' : '#00C49F', fontWeight: 600, marginLeft: '8px' }}>
              {formatarValor(valorReal)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const COLORS = ['#1e88e5', '#43a047', '#9c27b0', '#ff9800', '#e53935'];

  const contasInvestimento = dadosPosicao?.contas_bancarias?.filter(
    c => classeConta(c) === 'investimento') || [];
  const contasCorrente = dadosPosicao?.contas_bancarias?.filter(
    c => classeConta(c) === 'corrente') || [];
  const totalCorrente = contasCorrente.reduce((sum, c) => sum + (c.saldo_disponivel || 0), 0);
  const totalInvestimento = contasInvestimento.reduce((sum, c) => sum + (c.saldo_disponivel || 0), 0);

  return (
    <div className="container-fluid mt-4 fmp-brand-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-balance-scale header-icon me-3"></i>
                    <h2 className="titulo-pagina m-0">Posição Financeira Semanal </h2>
                  </div>
                  <p className="mb-1 opacity-75">
                    <i className="fas fa-calculator me-2"></i>
                    Detalhamento de contas a pagar e receber para o período selecionado + saldo projetado
                  </p>
                </div>
                
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-light"
                     onClick={irParaDashboard}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Voltar ao Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ABAS DE NAVEGAÇÃO */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs nav-fill">
            <li className="nav-item">
              <button 
                className={`nav-link ${abaAtiva === 'posicao' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('posicao')}
                style={{
                  backgroundColor: abaAtiva === 'posicao' ? '#00205b' : 'transparent',
                  color: abaAtiva === 'posicao' ? '#ffffff' : '#00205b',
                  fontWeight: 600,
                  border: abaAtiva === 'posicao' ? 'none' : '1px solid #dee2e6'
                }}
              >
                <i className="fas fa-chart-line me-2"></i>
                Calcular Posição
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${abaAtiva === 'historico' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('historico')}
                style={{
                  backgroundColor: abaAtiva === 'historico' ? '#00205b' : 'transparent',
                  color: abaAtiva === 'historico' ? '#ffffff' : '#00205b',
                  fontWeight: 600,
                  border: abaAtiva === 'historico' ? 'none' : '1px solid #dee2e6'
                }}
              >
                <i className="fas fa-history me-2"></i>
                Histórico de Envios ({historico.length})
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* CONTEÚDO DA ABA POSIÇÃO */}
      {abaAtiva === 'posicao' && (
        <>
          {/* Mensagens agora são exibidas como toast (react-toastify) */}

          {/* Seleção de Período */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-calendar-week me-2 text-primary"></i>
                    Selecionar Período de Análise
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">
                        <i className="fas fa-calendar-alt me-1"></i>
                        Data Inicial
                      </label>
                      <input 
                        type="date" 
                        className="form-control"
                        value={dataInicial}
                        onChange={(e) => setDataInicial(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">
                        <i className="fas fa-calendar-alt me-1"></i>
                        Data Final
                      </label>
                      <input 
                        type="date" 
                        className="form-control"
                        value={dataFinal}
                        onChange={(e) => setDataFinal(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <button 
                        className="btn w-100 btn-azul-sistema"
                        onClick={consultarPosicao}
                        disabled={carregando}
                        style={{
                          backgroundColor: '#00205b',
                          borderColor: '#00205b',
                          color: '#ffffff',
                          fontWeight: 600
                        }}
                      >
                        <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-search'} me-2`}></i>
                        {carregando ? 'Calculando...' : 'Consultar Posição'}
                      </button>
                    </div>
                    <div className="col-md-3">
                      <button 
                        className="btn btn-outline-secondary w-100"
                        onClick={() => {
                          const hoje = new Date();
                          const inicioSemana = new Date(hoje);
                          inicioSemana.setDate(hoje.getDate() - hoje.getDay() + 1);
                          const fimSemana = new Date(inicioSemana);
                          fimSemana.setDate(inicioSemana.getDate() + 6);
                          setDataInicial(formatarDataInput(inicioSemana));
                          setDataFinal(formatarDataInput(fimSemana));
                        }}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Semana Atual
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading */}
          {carregando && (
            <div className="row">
              <div className="col-12">
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}></div>
                  <p className="mt-3 text-muted">
                    <i className="fas fa-database me-2"></i>
                    Consultando banco de dados...
                  </p>
                  <div className="progress mt-3" style={{height: '6px', maxWidth: '400px', margin: '0 auto'}}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{width: '100%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resultados */}
          {dadosPosicao && !carregando && (
            <>
              {/* Aviso de configuração de contas */}
              <div className="mb-3 p-4 rounded-3 d-flex align-items-start gap-3"
                style={{ background: '#eef2f8', border: '1px solid #d6deeb', boxShadow: '0 2px 10px rgba(0,32,91,0.06)' }}>
                <div style={{ fontSize: '2rem', lineHeight: 1, color: '#00205b' }}>
                  <i className="fas fa-info-circle"></i>
                </div>
                <div>
                  <p className="fw-bold mb-1" style={{ fontSize: '1rem', letterSpacing: '0.3px', color: '#00205b' }}>
                    Controle de Contas na Posição Financeira
                  </p>
                  <p className="mb-0" style={{ fontSize: '0.92rem', color: '#3a4a66' }}>
                    Caso queira que alguma conta <strong>não apareça</strong> nesta posição, acesse{' '}
                    <span style={{ background: 'rgba(0,32,91,0.08)', borderRadius: '4px', padding: '1px 6px' }}>
                      Configurações → Config. Contas
                    </span>{' '}
                    e altere o campo <strong>"Posição Financeira"</strong> para <strong>Não</strong>.
                  </p>
                </div>
              </div>

              {/* Cards de Resumo */}
              <div className="row mb-3">
                {/* Conta Corrente */}
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-primary border-4">
                    <div className="card-header bg-white py-2">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-university me-2 text-primary"></i>
                        Conta Corrente / Digital
                      </h6>
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Disponível para pagar
                      </small>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <h3 className="text-primary mb-0">{formatarValor(totalCorrente)}</h3>
                        <small className="text-muted">{contasCorrente.length} conta(s)</small>
                      </div>
                      {contasCorrente.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <tbody>
                              {contasCorrente.map((conta, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <small className="fw-bold">{conta.nome}</small>
                                    {conta.tipo && (
                                      <div>
                                        <span className="badge bg-primary bg-opacity-50" style={{fontSize: '10px'}}>{conta.tipo}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <strong className="text-primary">{formatarValor(conta.saldo_disponivel)}</strong>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="table-primary">
                                <td><small><strong>TOTAL</strong></small></td>
                                <td className="text-end"><small><strong>{formatarValor(totalCorrente)}</strong></small></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Investimentos */}
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-info border-4">
                    <div className="card-header bg-white py-2">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-piggy-bank me-2 text-info"></i>
                        Investimentos / Aplicações
                      </h6>
                      <small className="text-info">
                        <i className="fas fa-undo me-1"></i>
                        Resgatável se precisar
                      </small>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <h3 className="text-info mb-0">{formatarValor(totalInvestimento)}</h3>
                        <small className="text-muted">{contasInvestimento.length} conta(s)</small>
                      </div>
                      {contasInvestimento.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <tbody>
                              {contasInvestimento.map((conta, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <small className="fw-bold">{conta.nome}</small>
                                    {conta.tipo && (
                                      <div>
                                        <span className="badge bg-info bg-opacity-50" style={{fontSize: '10px'}}>{conta.tipo}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <strong className="text-info">{formatarValor(conta.saldo_disponivel)}</strong>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="table-info">
                                <td><small><strong>TOTAL</strong></small></td>
                                <td className="text-end"><small><strong>{formatarValor(totalInvestimento)}</strong></small></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-3 text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          <small>Nenhum investimento cadastrado</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contas a Receber */}
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-success border-4">
                    <div className="card-body text-center">
                      <i className="fas fa-plus-circle fa-2x text-success mb-2"></i>
                      <h6 className="text-muted small">Contas a Receber</h6>
                      <h4 className="text-success mb-0">
                        {formatarValor(dadosPosicao.resumo_posicao.total_contas_receber)}
                      </h4>
                      <small className="text-muted">
                        {/* o valor acima é só "a vencer + vence hoje" (vencidas têm selo
                            próprio), então a contagem precisa somar o mesmo conjunto —
                            antes vinha qtd_contas_receber, que incluía as vencidas */}
                        {(dadosPosicao.resumo_posicao.qtd_contas_receber_a_vencer || 0)
                          + (dadosPosicao.resumo_posicao.qtd_contas_receber_vence_hoje || 0)} títulos a vencer
                      </small>
                      {dadosPosicao.resumo_posicao.contas_receber_vencidas > 0 && (
                        <div className="mt-2">
                          <span className="badge bg-danger">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formatarValor(dadosPosicao.resumo_posicao.contas_receber_vencidas)} vencidas
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contas a Pagar */}
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-danger border-4">
                    <div className="card-body text-center">
                      <i className="fas fa-minus-circle fa-2x text-danger mb-2"></i>
                      <h6 className="text-muted small">Contas a Pagar</h6>
                      <h4 className="text-danger mb-0">
                        {formatarValor(dadosPosicao.resumo_posicao.total_contas_pagar)}
                      </h4>
                      <small className="text-muted">
                        {/* mesma correção do card a receber: o valor é só "a vencer +
                            vence hoje", então a contagem não pode incluir as vencidas */}
                        {(dadosPosicao.resumo_posicao.qtd_contas_pagar_a_vencer || 0)
                          + (dadosPosicao.resumo_posicao.qtd_contas_pagar_vence_hoje || 0)} títulos a vencer
                      </small>
                      {dadosPosicao.resumo_posicao.contas_pagar_vencidas > 0 && (
                        <div className="mt-2">
                          <span className="badge bg-danger">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            {formatarValor(dadosPosicao.resumo_posicao.contas_pagar_vencidas)} vencidas
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Saldo Final e Informações */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm border-start border-warning border-4">
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-3 text-center border-end">
                          <small className="text-muted d-block">Saldo Base (Contas Correntes)</small>
                          <h4 className="text-primary mb-0">{formatarValor(dadosPosicao.resumo_posicao.saldo_contas_correntes)}</h4>
                        </div>
                        <div className="col-md-3 text-center border-end">
                          <small className="text-muted d-block">Saldo Final Projetado</small>
                          <h3 className={`mb-0 ${dadosPosicao.resumo_posicao.saldo_final_projetado >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(dadosPosicao.resumo_posicao.saldo_final_projetado)}
                          </h3>
                          <small className={dadosPosicao.resumo_posicao.variacao >= 0 ? 'text-success' : 'text-danger'}>
                            {dadosPosicao.resumo_posicao.variacao >= 0 ? '+' : ''}
                            {formatarValor(dadosPosicao.resumo_posicao.variacao)}
                            {' '}({dadosPosicao.resumo_posicao.variacao_percentual >= 0 ? '+' : ''}{dadosPosicao.resumo_posicao.variacao_percentual.toFixed(1)}%)
                          </small>
                        </div>
                        <div className="col-md-3 text-center border-end">
                          <small className="text-muted d-block">Patrimônio Total (Ref.)</small>
                          <h4 className="text-info mb-0">{formatarValor(dadosPosicao.resumo_posicao.total_patrimonio)}</h4>
                          <small className="text-muted">Corrente + Investimentos</small>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted d-block mb-1">
                            <strong>Fórmula:</strong> Corrente - A Pagar + A Receber
                          </small>
                          <small className="text-muted d-block mb-1">
                            <strong>Status:</strong> ABERTO, A VENCER, VENCIDO, ATRASADO
                          </small>
                          <small className="text-muted d-block">
                            <strong>Contas:</strong> {dadosPosicao.contas_bancarias.map(c => c.nome).join(', ')}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráficos */}
              <div className="row mb-4">
                {/* Diversificação */}
                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-pie me-2 text-success"></i>
                        Diversificação
                      </h5>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={dadosPosicao.contas_bancarias
                              .filter(c => c.saldo_disponivel !== 0)
                              .map(c => ({ nome: c.nome, valor: Math.abs(c.saldo_disponivel), valorReal: c.saldo_disponivel, cor: c.cor }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            innerRadius={50}
                            dataKey="valor"
                            label={({ nome, percent, valorReal }) => `${nome}${valorReal < 0 ? ' (-)' : ''}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {dadosPosicao.contas_bancarias
                              .filter(c => c.saldo_disponivel !== 0)
                              .map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.saldo_disponivel < 0 ? '#ff6b6b' : (entry.cor || COLORS[index % COLORS.length])} />
                              ))}
                          </Pie>
                          <Tooltip content={<CustomTooltipDiversificacao />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Fluxo Waterfall */}
                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2" style={{ color: '#00205b' }}></i>
                        Fluxo da Posição Financeira (Base: Contas Correntes/Digitais)
                      </h5>
                    </div>
                    <div className="card-body" ref={waterfallChartRef}>
                      <ResponsiveContainer width="100%" height={380}>
                        <ComposedChart data={dadosPosicao.dados_waterfall} margin={{ top: 40, right: 24, left: 12, bottom: 10 }}>
                          <defs>
                            <linearGradient id="wfCyan" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#22c9e0" /><stop offset="100%" stopColor="#00a6bd" />
                            </linearGradient>
                            <linearGradient id="wfGreen" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#2ecb8b" /><stop offset="100%" stopColor="#1fa971" />
                            </linearGradient>
                            <linearGradient id="wfRed" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f26a72" /><stop offset="100%" stopColor="#e5484d" />
                            </linearGradient>
                            <linearGradient id="wfNavy" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#1a3f86" /><stop offset="100%" stopColor="#00205b" />
                            </linearGradient>
                          </defs>
                          <CartesianGrid vertical={false} stroke="#eef1f6" strokeDasharray="4 4" />

                          <XAxis
                            dataKey="item"
                            tick={{ fontSize: 12.5, fill: '#475467', fontWeight: 600 }}
                            angle={-12}
                            textAnchor="end"
                            height={80}
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                          />

                          <YAxis
                            tickFormatter={(value) => formatarValorCompacto(value)}
                            tick={{ fontSize: 12, fill: '#98a4b8' }}
                            axisLine={false}
                            tickLine={false}
                            width={78}
                          />

                          <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'rgba(0,32,91,0.04)' }} />

                          <Bar dataKey="valor" radius={[8, 8, 0, 0]} maxBarSize={96}>
                            {dadosPosicao.dados_waterfall.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={corBarraWaterfall(entry.item, index)} />
                            ))}
                            <LabelList
                              dataKey="valor"
                              position="top"
                              offset={14}
                              formatter={(value) => formatarValor(value)}
                              style={{ fill: '#00205b', fontSize: 13, fontWeight: 700 }}
                            />
                          </Bar>

                          <Line
                            type="monotone"
                            dataKey="valor"
                            stroke="#00205b"
                            strokeWidth={2.5}
                            dot={{ r: 5, fill: '#ffffff', stroke: '#00205b', strokeWidth: 2 }}
                            activeDot={{ r: 7, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* CAMPO OBSERVAÇÃO + BOTÃO ENVIAR EMAIL */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-envelope me-2 text-primary"></i>
                        Enviar Posição por Email
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-12 mb-3">
                          <label className="form-label fw-semibold d-flex align-items-center gap-2" style={{ color: '#2b2f38' }}>
                            <i className="fas fa-comment-dots" style={{ color: '#00205b' }}></i>
                            Observações Adicionais
                            <span className="text-muted fw-normal" style={{ fontSize: '0.85rem' }}>(opcional)</span>
                          </label>
                          <textarea
                            className="form-control posicao-obs-textarea"
                            rows="4"
                            placeholder="Ex: Faltam boletos do fornecedor X, pagamento agendado para o dia Y, aguardando confirmação de recebimento Z..."
                            value={observacao}
                            onChange={(e) => setObservacao(e.target.value)}
                            disabled={enviandoEmail}
                            spellCheck="true"
                            lang="pt-BR"
                            autoCorrect="on"
                            autoCapitalize="sentences"
                          />
                          <div className="d-flex justify-content-between align-items-center mt-1">
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              Esta observação será incluída no email enviado ao cliente
                            </small>
                            <small className="text-muted">{observacao.length} caractere(s)</small>
                          </div>
                        </div>
                      </div>

                      <div className="text-center">
                        <button
                          className="btn-enviar-playful"
                          onClick={abrirPreviewEmail}
                          disabled={enviandoEmail || carregandoPreview}
                        >
                          <span>
                            <i className={`fas ${enviandoEmail ? 'fa-spinner fa-spin' : 'fa-envelope'}`}></i>
                            {enviandoEmail ? 'ENVIANDO EMAIL...' : 'ENVIAR POSIÇÃO POR EMAIL'}
                          </span>
                        </button>
                        
                        {/* Mensagens agora são exibidas como toast (react-toastify) */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal de confirmação antes de enviar o e-mail */}
              {mostrarConfirmacao && (
                <>
                  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
                      <div className="modal-content">
                        <div className="modal-header bg-white">
                          <div>
                            <h5 className="modal-title" style={{ color: '#212529' }}>
                              <i className="fas fa-envelope-open-text me-2" style={{ color: '#00205b' }}></i>
                              Prévia do e-mail
                            </h5>
                            <small style={{ color: '#6c757d' }}>É exatamente assim que vai chegar para o cliente. Confirme antes de enviar.</small>
                          </div>
                          <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setMostrarConfirmacao(false)}></button>
                        </div>

                        <div className="modal-body" style={{ background: '#f0f2f5' }}>
                          {/* Cabeçalho estilo e-mail: Para / Assunto */}
                          <div className="mb-3 p-3 rounded bg-white border">
                            <div className="mb-1">
                              <span className="text-muted" style={{ width: '70px', display: 'inline-block' }}>Para:</span>
                              <strong>{previewDest || '—'}</strong>
                            </div>
                            <div>
                              <span className="text-muted" style={{ width: '70px', display: 'inline-block' }}>Assunto:</span>
                              <strong>{previewAssunto || '—'}</strong>
                            </div>
                          </div>

                          {carregandoPreview ? (
                            <div className="text-center py-5">
                              <div className="spinner-border" style={{ color: '#00205b', width: '3rem', height: '3rem' }}></div>
                              <p className="mt-3 text-muted">Gerando prévia do e-mail...</p>
                            </div>
                          ) : (
                            <iframe
                              title="Prévia do e-mail"
                              srcDoc={previewHtml}
                              style={{ width: '100%', height: '65vh', border: '1px solid #e7e9ee', borderRadius: '8px', background: '#ffffff' }}
                            />
                          )}
                        </div>

                        <div className="modal-footer bg-white" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '14px' }}>
                          <label
                            htmlFor="confirmaInfosPosicao"
                            className="d-flex align-items-center gap-3 w-100 m-0"
                            style={{
                              background: confirmacaoMarcada ? '#e8f5ee' : '#eef2f8',
                              border: `2px solid ${confirmacaoMarcada ? '#1f9d6b' : '#00205b'}`,
                              borderRadius: '10px',
                              padding: '14px 18px',
                              cursor: (carregandoPreview || !previewHtml || enviandoEmail) ? 'not-allowed' : 'pointer',
                              transition: 'background-color .2s ease, border-color .2s ease'
                            }}
                          >
                            <input
                              className="form-check-input m-0"
                              type="checkbox"
                              id="confirmaInfosPosicao"
                              style={{ width: '22px', height: '22px', flex: 'none', cursor: 'inherit' }}
                              checked={confirmacaoMarcada}
                              disabled={carregandoPreview || !previewHtml || enviandoEmail}
                              onChange={(e) => setConfirmacaoMarcada(e.target.checked)}
                            />
                            <span style={{ fontSize: '15px', fontWeight: 600, color: confirmacaoMarcada ? '#1f7a52' : '#00205b' }}>
                              <i className={`fas ${confirmacaoMarcada ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                              Confirmo que revisei a prévia e as informações estão corretas
                            </span>
                          </label>

                          <div className="d-flex justify-content-end gap-2 w-100">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setMostrarConfirmacao(false)} disabled={enviandoEmail}>
                              <i className="fas fa-pen me-2"></i>Revisar
                            </button>
                            <button
                              type="button"
                              className="btn"
                              style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#ffffff', fontWeight: 600 }}
                              onClick={enviarPosicaoEmail}
                              disabled={enviandoEmail || carregandoPreview || !previewHtml || !confirmacaoMarcada}
                            >
                              <i className={`fas ${enviandoEmail ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>
                              {enviandoEmail ? 'Enviando...' : 'Confirmar e Enviar'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-backdrop fade show"></div>
                </>
              )}

              {/* Análise */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="alert border-start border-4" style={{borderColor: '#00205b', background: '#f7f8fa', color: '#2b2f38'}}>
                    <h5 className="alert-heading">
                      <i className="fas fa-lightbulb me-2"></i>
                      Análise da Posição Financeira
                    </h5>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-2">
                          <strong>Situação:</strong> 
                          {dadosPosicao.resumo_posicao.saldo_final_projetado > dadosPosicao.resumo_posicao.saldo_inicial
                            ? ' Posição financeira POSITIVA para o período.'
                            : ' Posição financeira NEGATIVA para o período.'}
                        </p>
                        <p className="mb-2">
                          <strong>Variação:</strong> {dadosPosicao.resumo_posicao.variacao >= 0 ? 'Aumento' : 'Redução'} de {formatarValor(Math.abs(dadosPosicao.resumo_posicao.variacao))} ({dadosPosicao.resumo_posicao.variacao_percentual >= 0 ? '+' : ''}{dadosPosicao.resumo_posicao.variacao_percentual.toFixed(1)}%)
                        </p>
                        <p className="mb-0">
                          <strong>Liquidez:</strong> 
                          {dadosPosicao.resumo_posicao.saldo_contas_correntes > dadosPosicao.resumo_posicao.total_contas_pagar
                            ? ' Saldo em contas suficiente para cobrir contas a pagar.'
                            : ' Saldo insuficiente. Considere resgatar investimentos ou aguardar recebimentos.'}
                        </p>
                      </div>
                      <div className="col-md-6">
                        {dadosPosicao.resumo_posicao.contas_pagar_vencidas > 0 && (
                          <p className="mb-2 text-danger">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            <strong>Urgente:</strong> {formatarValor(dadosPosicao.resumo_posicao.contas_pagar_vencidas)} em {dadosPosicao.resumo_posicao.qtd_contas_pagar_vencidas} conta(s) vencida(s)/atrasada(s)!
                          </p>
                        )}
                        {dadosPosicao.resumo_posicao.contas_receber_vencidas > 0 && (
                          <p className="mb-2 text-warning">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            <strong>Cobrança:</strong> {formatarValor(dadosPosicao.resumo_posicao.contas_receber_vencidas)} em {dadosPosicao.resumo_posicao.qtd_contas_receber_vencidas} conta(s) vencida(s) a receber.
                          </p>
                        )}
                        {dadosPosicao.resumo_posicao.saldo_final_projetado < 0 && (
                          <p className="mb-0 text-danger">
                            <i className="fas fa-times-circle me-1"></i>
                            <strong>Atenção:</strong> Saldo final negativo. Resgate de investimentos necessário!
                          </p>
                        )}
                        {dadosPosicao.resumo_posicao.saldo_final_projetado >= dadosPosicao.resumo_posicao.saldo_inicial && (
                          <p className="mb-0 text-success">
                            <i className="fas fa-check-circle me-1"></i>
                           <strong>Positivo:</strong> Posição financeira melhora no período!
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhamento das Contas */}
              <div className="row mb-4">
                {/* Contas a Pagar */}
                <div className="col-lg-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-file-invoice-dollar me-2 text-danger"></i>
                        Contas a Pagar ({dadosPosicao.resumo_posicao.qtd_contas_pagar})
                      </h5>
                      <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setMostrarContasPagar(!mostrarContasPagar)}
                      >
                        <i className={`fas fa-chevron-${mostrarContasPagar ? 'up' : 'down'}`}></i>
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-6 text-center border-end">
                          <small className="text-muted d-block">Vencidas/Atrasadas</small>
                          <h5 className="text-danger mb-1">
                            {formatarValor(dadosPosicao.resumo_posicao.contas_pagar_vencidas)}
                          </h5>
                          <small className="text-muted">
                            {dadosPosicao.resumo_posicao.qtd_contas_pagar_vencidas} títulos
                          </small>
                        </div>
                        <div className="col-6 text-center">
                          <small className="text-muted d-block">A Vencer (período)</small>
                          <h5 className="text-warning mb-1">
                            {formatarValor((dadosPosicao.resumo_posicao.contas_pagar_a_vencer || 0) + (dadosPosicao.resumo_posicao.contas_pagar_vence_hoje || 0))}
                          </h5>
                          <small className="text-muted">
                            {(dadosPosicao.resumo_posicao.qtd_contas_pagar_a_vencer || 0) + (dadosPosicao.resumo_posicao.qtd_contas_pagar_vence_hoje || 0)} títulos
                          </small>
                        </div>
                      </div>
                      
                      {mostrarContasPagar && (
                        <>
                          {selPagar.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mb-2 p-2 rounded" style={{background: '#fff5f5'}}>
                              <small className="text-danger fw-bold">
                                <i className="fas fa-check-square me-1"></i>
                                {selPagar.length} selecionado(s)
                              </small>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deletarSelecionados('pagar')}
                                disabled={deletando}
                              >
                                <i className="fas fa-trash-alt me-1"></i>
                                {deletando ? 'Excluindo...' : 'Excluir selecionados'}
                              </button>
                            </div>
                          )}
                          <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th width="4%" className="text-center"><i className="fas fa-trash-alt text-muted"></i></th>
                                  <th width="13%">Vencimento</th>
                                  <th width="27%">Fornecedor</th>
                                  <th width="15%" className="text-end">Valor</th>
                                  <th width="12%" className="text-center">Status</th>
                                  <th width="11%" className="text-center">{sistemaCliente === 'conta_azul' ? 'Conta Azul' : 'Omie'}</th>
                                  <th width="18%" className="text-center" style={{ background: '#eaf2ff', color: '#00205b' }}>
                                    <i className="fas fa-calendar-check me-1"></i>Agendado?
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {dadosPosicao.contas_pagar.map((conta, index) => (
                                  <tr
                                    key={index}
                                    className={selPagar.includes(conta.codigo_lancamento) ? 'table-active' : ''}
                                    style={agendados.includes(conta.codigo_lancamento) ? { background: 'rgba(31,169,113,0.10)' } : undefined}
                                  >
                                    <td className="text-center align-middle">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selPagar.includes(conta.codigo_lancamento)}
                                        disabled={!conta.codigo_lancamento}
                                        onChange={() => toggleSel('pagar', conta.codigo_lancamento)}
                                      />
                                    </td>
                                    <td>
                                      <small>{conta.vencimento}</small>
                                      {conta.dias_atraso > 0 && (
                                        <div className="text-danger small">
                                          <i className="fas fa-clock me-1"></i>
                                          {conta.dias_atraso} dias
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <small>{conta.fornecedor}</small>
                                      {conta.numero && conta.numero !== 'S/N' && (
                                        <div className="text-muted" style={{fontSize: '10px'}}>
                                          {conta.numero}
                                        </div>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      <strong className="text-danger">{formatarValor(conta.valor)}</strong>
                                    </td>
                                    <td className="text-center">
                                      <span className={`badge ${conta.status === 'VENCIDA/ATRASADA' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                        {conta.status === 'VENCIDA/ATRASADA' ? 'VENCIDA' : conta.status}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <small className="text-muted">{conta.status_omie}</small>
                                    </td>
                                    <td className="text-center align-middle">
                                      {agendados.includes(conta.codigo_lancamento) ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm p-1"
                                          style={{ background: '#1fa971', color: '#fff', borderRadius: 8, fontSize: 11, fontWeight: 600, lineHeight: 1 }}
                                          title="Agendado no banco — clique para desmarcar"
                                          onClick={() => toggleAgendado(conta.codigo_lancamento)}
                                          disabled={!conta.codigo_lancamento}
                                        >
                                          <i className="fas fa-calendar-check me-1"></i>Agendado
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary p-1"
                                          style={{ borderRadius: 8, fontSize: 11, lineHeight: 1 }}
                                          title="Marcar como agendado no banco"
                                          onClick={() => toggleAgendado(conta.codigo_lancamento)}
                                          disabled={!conta.codigo_lancamento}
                                        >
                                          <i className="far fa-square me-1"></i>Marcar
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contas a Receber */}
                <div className="col-lg-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-hand-holding-usd me-2 text-success"></i>
                        Contas a Receber ({dadosPosicao.resumo_posicao.qtd_contas_receber})
                        <small className="text-muted d-block">Todo o período</small>
                      </h5>
                      <button 
                        className="btn btn-sm btn-outline-success"
                        onClick={() => setMostrarContasReceber(!mostrarContasReceber)}
                      >
                        <i className={`fas fa-chevron-${mostrarContasReceber ? 'up' : 'down'}`}></i>
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-6 text-center border-end">
                          <small className="text-muted d-block">Vencidas/Atrasadas</small>
                          <h5 className="text-danger mb-1">
                            {formatarValor(dadosPosicao.resumo_posicao.contas_receber_vencidas)}
                          </h5>
                          <small className="text-muted">
                            {dadosPosicao.resumo_posicao.qtd_contas_receber_vencidas} títulos
                          </small>
                        </div>
                        <div className="col-6 text-center">
                          <small className="text-muted d-block">A Vencer</small>
                          <h5 className="text-success mb-1">
                            {formatarValor((dadosPosicao.resumo_posicao.contas_receber_a_vencer || 0) + (dadosPosicao.resumo_posicao.contas_receber_vence_hoje || 0))}
                          </h5>
                          <small className="text-muted">
                            {(dadosPosicao.resumo_posicao.qtd_contas_receber_a_vencer || 0) + (dadosPosicao.resumo_posicao.qtd_contas_receber_vence_hoje || 0)} títulos
                          </small>
                        </div>
                      </div>
                      
                      {mostrarContasReceber && (
                        <>
                          {selReceber.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mb-2 p-2 rounded" style={{background: '#f0fff4'}}>
                              <small className="text-success fw-bold">
                                <i className="fas fa-check-square me-1"></i>
                                {selReceber.length} selecionado(s)
                              </small>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deletarSelecionados('receber')}
                                disabled={deletando}
                              >
                                <i className="fas fa-trash-alt me-1"></i>
                                {deletando ? 'Excluindo...' : 'Excluir selecionados'}
                              </button>
                            </div>
                          )}
                          <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th width="5%" className="text-center"><i className="fas fa-trash-alt text-muted"></i></th>
                                  <th width="16%">Previsão</th>
                                  <th width="32%">Cliente</th>
                                  <th width="17%" className="text-end">Valor</th>
                                  <th width="15%" className="text-center">Status</th>
                                  <th width="15%" className="text-center">{sistemaCliente === 'conta_azul' ? 'Conta Azul' : 'Omie'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dadosPosicao.contas_receber.map((conta, index) => (
                                  <tr key={index} className={selReceber.includes(conta.codigo_lancamento) ? 'table-active' : ''}>
                                    <td className="text-center align-middle">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selReceber.includes(conta.codigo_lancamento)}
                                        disabled={!conta.codigo_lancamento}
                                        onChange={() => toggleSel('receber', conta.codigo_lancamento)}
                                      />
                                    </td>
                                    <td>
                                      <small>{conta.vencimento}</small>
                                      {conta.dias_atraso > 0 && (
                                        <div className="text-danger small">
                                          <i className="fas fa-clock me-1"></i>
                                          {conta.dias_atraso} dias
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <small>{conta.cliente}</small>
                                      {conta.numero && conta.numero !== 'S/N' && (
                                        <div className="text-muted" style={{fontSize: '10px'}}>
                                          {conta.numero}
                                        </div>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      <strong className="text-success">{formatarValor(conta.valor)}</strong>
                                    </td>
                                    <td className="text-center">
                                      <span className={`badge ${conta.status === 'VENCIDA/ATRASADA' ? 'bg-danger' : 'bg-success'}`}>
                                        {conta.status === 'VENCIDA/ATRASADA' ? 'VENCIDA' : conta.status}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <small className="text-muted">{conta.status_omie}</small>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo Executivo */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-clipboard-check me-2 text-primary"></i>
                        Resumo Executivo da Posição
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <h6 className="text-muted mb-3">Análise de Liquidez (Contas Correntes/Digitais)</h6>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>Saldo Disponível:</span>
                              <strong className="text-primary">
                                {formatarValor(dadosPosicao.resumo_posicao.saldo_contas_correntes)}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>Compromissos Urgentes:</span>
                              <strong className="text-danger">
                                {formatarValor(dadosPosicao.resumo_posicao.contas_pagar_vencidas)}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>A Pagar no Período:</span>
                              <strong className="text-warning">
                                {formatarValor((dadosPosicao.resumo_posicao.contas_pagar_a_vencer || 0) + (dadosPosicao.resumo_posicao.contas_pagar_vence_hoje || 0))}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span><strong>Sobra/Falta em Conta:</strong></span>
                              <strong className={dadosPosicao.resumo_posicao.saldo_contas_correntes - dadosPosicao.resumo_posicao.total_contas_pagar >= 0 ? 'text-success' : 'text-danger'}>
                                {formatarValor(dadosPosicao.resumo_posicao.saldo_contas_correntes - dadosPosicao.resumo_posicao.total_contas_pagar)}
                              </strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-muted mb-3">Análise de Recebimentos</h6>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>Total a Receber:</span>
                              <strong className="text-success">
                                {formatarValor(dadosPosicao.resumo_posicao.total_contas_receber)}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>Recebimentos Atrasados:</span>
                              <strong className="text-danger">
                                {formatarValor(dadosPosicao.resumo_posicao.contas_receber_vencidas)}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span>A Receber (futuro):</span>
                              <strong className="text-info">
                                {formatarValor((dadosPosicao.resumo_posicao.contas_receber_a_vencer || 0) + (dadosPosicao.resumo_posicao.contas_receber_vence_hoje || 0))}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span><strong>Impacto Positivo:</strong></span>
                              <strong className="text-success">
                                +{formatarValor(dadosPosicao.resumo_posicao.total_contas_receber)}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* CONTEÚDO DA ABA HISTÓRICO */}
      {abaAtiva === 'historico' && (
        <>
          {/* Mensagens agora são exibidas como toast (react-toastify) */}

          {/* Botão Atualizar */}
          <div className="row mb-3">
            <div className="col-12 text-end">
              <button 
                className="btn btn-outline-primary"
                onClick={buscarHistorico}
                disabled={carregandoHistorico}
              >
                <i className={`fas ${carregandoHistorico ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                Atualizar Histórico
              </button>
            </div>
          </div>

          {/* Loading Histórico */}
          {carregandoHistorico && (
            <div className="row">
              <div className="col-12">
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}></div>
                  <p className="mt-3 text-muted">
                    <i className="fas fa-database me-2"></i>
                    Carregando histórico...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabela de Histórico */}
          {!carregandoHistorico && historico.length > 0 && (
            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-file-alt me-2 text-primary"></i>
                      Histórico de Posições Enviadas
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th width="8%">ID</th>
                            <th width="12%">Data Envio</th>
                            <th width="15%">Período</th>
                            <th width="12%" className="text-end">Saldo Inicial</th>
                            <th width="12%" className="text-end">Saldo Final</th>
                            <th width="12%" className="text-end">Total Pagar</th>
                            <th width="12%" className="text-end">Total Receber</th>
                            <th width="17%">Emails Enviados</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historico.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <span className="badge bg-secondary">#{item.id}</span>
                              </td>
                              <td>
                                <small>
                                  <i className="fas fa-calendar me-1 text-muted"></i>
                                  {new Date(item.data_envio).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </small>
                              </td>
                              <td>
                                <small>
                                  {item.periodo_inicio} até<br/>{item.periodo_fim}
                                </small>
                              </td>
                              <td className="text-end">
                                <strong className="text-primary">
                                  {formatarValor(item.saldo_inicial)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className={item.saldo_final >= 0 ? 'text-success' : 'text-danger'}>
                                  {formatarValor(item.saldo_final)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-danger">
                                  {formatarValor(item.total_pagar)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-success">
                                  {formatarValor(item.total_receber)}
                                </strong>
                              </td>
                              <td>
                                <small className="text-muted">
                                  <i className="fas fa-envelope me-1"></i>
                                  {item.emails_enviados}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mensagem quando não há histórico */}
          {!carregandoHistorico && historico.length === 0 && (
            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center py-5">
                    <i className="fas fa-inbox fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">Nenhum histórico encontrado</h5>
                    <p className="text-muted">Calcule e envie uma posição financeira para começar o histórico</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Rodapé */}
      <div className="row mt-4 mb-3">
        <div className="col-12">
          <div className="card border-0 bg-light">
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-university me-1 text-primary"></i>
                    <strong>Base:</strong> Contas Correntes/Digitais
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-hand-holding-usd me-1 text-success"></i>
                    <strong>Receber:</strong> Todo período
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-file-invoice-dollar me-1 text-danger"></i>
                    <strong>Pagar:</strong> Vencidas + Período
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-exclamation-triangle me-1 text-warning"></i>
                    <strong>Status:</strong> Inclui ATRASADO
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-wallet me-1 text-info"></i>
                    <strong>Saldo:</strong> Disponível
                  </small>
                </div>
                <div className="col-md-2">
                  <small className="text-muted">
                    <i className="fas fa-clock me-1 text-secondary"></i>
                    <strong>Update:</strong> {new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Posicao_cliente;